import { z } from 'zod';

const AGENT_AFFECTED_REF_KINDS = [
  'doc',
  'block',
  'canvas_element',
  'workflow',
  'node',
] as const;

/** A control in the header of a database view a change set (filter, sort, group). */
export const AGENT_VIEW_CONTROLS = ['filter', 'sort', 'group'] as const;

export const AgentAffectedRefSchema = z.object({
  kind: z.enum(AGENT_AFFECTED_REF_KINDS),
  workspaceId: z.string(),
  docId: z.string().optional(),
  blockId: z.string().optional(),
  /** A row of the database the block is, and the column of one cell of it. */
  rowId: z.string().optional(),
  columnId: z.string().optional(),
  /** The header control of the database view the change set. */
  viewControl: z.enum(AGENT_VIEW_CONTROLS).optional(),
  elementId: z.string().optional(),
  workflowId: z.string().optional(),
  nodeId: z.string().optional(),
});

export type AgentAffectedRef = z.infer<typeof AgentAffectedRefSchema>;

/**
 * Who made a change: a crewmate with its own name (a workspace or marketplace
 * crewmate), or the owner's own crewmate of a thread, which has no name of
 * its own. The client words both; the owner is `ownerUserId`.
 */
export const AgentIdentitySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('named'), name: z.string().min(1) }),
  z.object({ kind: z.literal('owner') }),
]);

export type AgentIdentity = z.infer<typeof AgentIdentitySchema>;

/** Provenance of one agent tool step carried by a live document update. */
export const AgentDocUpdateSchema = z.object({
  runId: z.string().min(1),
  stepId: z.string().min(1),
  ownerUserId: z.string().min(1),
  agent: AgentIdentitySchema,
  targets: z.array(AgentAffectedRefSchema),
});

export type AgentDocUpdate = z.infer<typeof AgentDocUpdateSchema>;

export const ClientActionRefSchema = z
  .object({
    sessionId: z.string().min(1).max(256),
    toolCallId: z.string().min(1).max(256),
    clientInstanceId: z.string().min(1).max(256),
    capability: z.string().uuid(),
    runId: z.string().min(1).max(256),
    stepId: z.string().min(1).max(256),
    targets: z.array(z.unknown()).optional(),
  })
  .strict();
export type ClientActionRef = z.infer<typeof ClientActionRefSchema>;

export const ClientActionAgentSchema = AgentDocUpdateSchema.extend({
  clientAction: ClientActionRefSchema,
});
export type ClientActionAgent = z.infer<typeof ClientActionAgentSchema>;

/** The realtime event that tells a workspace an agent run ended. */
export const AGENT_RUN_ENDED_EVENT = 'agent:run-ended';

/**
 * An agent run reached its stored end (completed, failed or cancelled): the
 * pointers of its changes leave the documents.
 */
export const AgentRunEndedSchema = z.object({
  workspaceId: z.string().min(1),
  runId: z.string().min(1),
});

export type AgentRunEnded = z.infer<typeof AgentRunEndedSchema>;
